import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  SimpleGrid,
  Container,
  Heading,
  useColorModeValue,
  Spinner,
  Center,
  IconButton,
  Tooltip,
  Alert,
  AlertIcon,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Textarea,
  Select,
  Checkbox,
  Image,
  FormControl,
  FormLabel
} from '@chakra-ui/react';
import { 
  FiArchive, 
  FiPlus, 
  FiEdit, 
  FiTrash2, 
  FiRefreshCw,
  FiClock,
  FiUser,
  FiRotateCcw,
  FiEye,
  FiCheck,
  FiDownload
} from 'react-icons/fi';
import { ContentManagementService, type ContentArchive } from '../services/contentManagementService';
import { useAuth } from '../contexts/AuthContext';
import IconWrapper from './IconWrapper';

interface ContentArchivingProps {
  showHeader?: boolean;
  userId?: string;
}

const ContentArchiving: React.FC<ContentArchivingProps> = ({
  showHeader = true,
  userId
}) => {
  const { user } = useAuth();
  const toast = useToast();
  const [archivedContent, setArchivedContent] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isUnarchiveModalOpen, setIsUnarchiveModalOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState<any | null>(null);
  const [isViewingArchive, setIsViewingArchive] = useState(false);
  const [bulkSelected, setBulkSelected] = useState<string[]>([]);

  // Form states
  const [archiveReason, setArchiveReason] = useState('');
  const [isPermanent, setIsPermanent] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const archiveReasons = [
    'Content no longer relevant',
    'Copyright issues',
    'Quality concerns',
    'User request',
    'Policy violation',
    'Technical issues',
    'Other'
  ];

  useEffect(() => {
    loadArchivedContent();
  }, [userId]);

  const loadArchivedContent = async () => {
    try {
      setIsLoading(true);
      const archivedData = await ContentManagementService.getArchivedContent(userId);
      setArchivedContent(archivedData);
    } catch (error) {
      console.error('Error loading archived content:', error);
      toast({
        title: 'Error loading archived content',
        description: 'Failed to load archived content. Please try again.',
        status: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleArchiveContent = async () => {
    if (!archiveReason.trim()) {
      toast({
        title: 'Archive reason required',
        description: 'Please provide a reason for archiving.',
        status: 'error'
      });
      return;
    }

    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please sign in to archive content.',
        status: 'error'
      });
      return;
    }

    try {
      setIsArchiving(true);
      await ContentManagementService.archiveContent(
        selectedContent.id,
        archiveReason,
        user.uid,
        isPermanent
      );
      
      toast({
        title: 'Content archived',
        description: 'Content has been archived successfully.',
        status: 'success'
      });
      
      setArchiveReason('');
      setIsPermanent(false);
      setIsArchiveModalOpen(false);
      setSelectedContent(null);
      await loadArchivedContent();
    } catch (error: any) {
      console.error('Error archiving content:', error);
      toast({
        title: 'Error archiving content',
        description: error.message || 'Failed to archive content.',
        status: 'error'
      });
    } finally {
      setIsArchiving(false);
    }
  };

  const handleUnarchiveContent = async () => {
    if (!selectedContent) return;

    try {
      await ContentManagementService.unarchiveContent(selectedContent.id);
      
      toast({
        title: 'Content unarchived',
        description: 'Content has been unarchived successfully.',
        status: 'success'
      });
      
      setIsUnarchiveModalOpen(false);
      setSelectedContent(null);
      await loadArchivedContent();
    } catch (error: any) {
      console.error('Error unarchiving content:', error);
      toast({
        title: 'Error unarchiving content',
        description: error.message || 'Failed to unarchive content.',
        status: 'error'
      });
    }
  };

  const handleBulkArchive = async () => {
    if (bulkSelected.length === 0) {
      toast({
        title: 'No content selected',
        description: 'Please select content to archive.',
        status: 'error'
      });
      return;
    }

    if (!user) {
      toast({
        title: 'Authentication required',
        description: 'Please sign in to archive content.',
        status: 'error'
      });
      return;
    }

    try {
      await ContentManagementService.bulkArchiveContent(
        bulkSelected,
        archiveReason,
        user.uid
      );
      
      toast({
        title: 'Content archived',
        description: `${bulkSelected.length} items have been archived successfully.`,
        status: 'success'
      });
      
      setBulkSelected([]);
      setArchiveReason('');
      await loadArchivedContent();
    } catch (error: any) {
      console.error('Error bulk archiving content:', error);
      toast({
        title: 'Error archiving content',
        description: error.message || 'Failed to archive content.',
        status: 'error'
      });
    }
  };

  const handleBulkSelection = (contentId: string, isSelected: boolean) => {
    if (isSelected) {
      setBulkSelected([...bulkSelected, contentId]);
    } else {
      setBulkSelected(bulkSelected.filter(id => id !== contentId));
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString() + ' ' + dateObj.toLocaleTimeString();
  };

  const getContentTypeColor = (category: string) => {
    switch (category) {
      case 'movie':
        return 'red';
      case 'series':
        return 'blue';
      case 'short':
        return 'green';
      case 'comic':
        return 'purple';
      case 'novel':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const renderContentRow = (content: any) => {
    const isSelected = bulkSelected.includes(content.id);
    
    return (
      <Tr key={content.id}>
        <Td>
          <Checkbox
            isChecked={isSelected}
            onChange={(e) => handleBulkSelection(content.id, e.target.checked)}
            colorScheme="purple"
          />
        </Td>
        <Td>
          <HStack gap={3}>
            <Image
              src={content.poster}
              alt={content.title}
              w="40px"
              h="40px"
              objectFit="cover"
              borderRadius="md"
              fallbackSrc="/placeholder-poster.jpg"
            />
            <VStack align="start" gap={1}>
              <Text fontWeight="medium" color="white" noOfLines={1}>
                {content.title}
              </Text>
              <Badge colorScheme={getContentTypeColor(content.category)} fontSize="xs">
                {content.category}
              </Badge>
            </VStack>
          </HStack>
        </Td>
        <Td>
          <Text fontSize="sm" color="gray.300" noOfLines={2}>
            {content.description}
          </Text>
        </Td>
        <Td>
          <Text fontSize="sm" color="gray.400">
            {formatDate(content.archivedAt)}
          </Text>
        </Td>
        <Td>
          <HStack gap={1}>
            <Tooltip label="View content">
              <IconButton
                size="sm"
                variant="ghost"
                icon={<IconWrapper icon={FiEye} size={14} />}
                onClick={() => {
                  setSelectedContent(content);
                  setIsViewingArchive(true);
                }}
                aria-label="View content"
              />
            </Tooltip>
            <Tooltip label="Unarchive content">
              <IconButton
                size="sm"
                variant="ghost"
                colorScheme="green"
                icon={<IconWrapper icon={FiRotateCcw} size={14} />}
                onClick={() => {
                  setSelectedContent(content);
                  setIsUnarchiveModalOpen(true);
                }}
                aria-label="Unarchive content"
              />
            </Tooltip>
          </HStack>
        </Td>
      </Tr>
    );
  };

  if (isLoading) {
    return (
      <Center py={8}>
        <Spinner size="lg" color="purple.400" />
      </Center>
    );
  }

  return (
    <Container maxW="container.xl">
      <VStack gap={6} align="stretch">
        {showHeader && (
          <HStack justify="space-between">
            <Heading size="lg" color="white">
              <IconWrapper icon={FiArchive} size={24} style={{ marginRight: '8px' }} />
              Content Archiving
            </Heading>
            <Tooltip label="Refresh archived content">
              <IconButton
                aria-label="Refresh"
                icon={<IconWrapper icon={FiRefreshCw} size={16} />}
                onClick={loadArchivedContent}
                variant="ghost"
                colorScheme="purple"
              />
            </Tooltip>
          </HStack>
        )}

        {/* Archive Statistics */}
        <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
          <Box bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
            <HStack gap={2}>
              <IconWrapper icon={FiArchive} size={20} color="orange.400" />
              <VStack align="start" gap={0}>
                <Text fontSize="2xl" fontWeight="bold" color="white">
                  {archivedContent.length}
                </Text>
                <Text fontSize="sm" color="gray.400">
                  Archived Items
                </Text>
              </VStack>
            </HStack>
          </Box>
          
          <Box bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
            <HStack gap={2}>
              <IconWrapper icon={FiClock} size={20} color="blue.400" />
              <VStack align="start" gap={0}>
                <Text fontSize="2xl" fontWeight="bold" color="white">
                  {archivedContent.filter(c => c.isPermanent).length}
                </Text>
                <Text fontSize="sm" color="gray.400">
                  Permanent Archives
                </Text>
              </VStack>
            </HStack>
          </Box>
          
          <Box bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
            <HStack gap={2}>
              <IconWrapper icon={FiDownload} size={20} color="green.400" />
              <VStack align="start" gap={0}>
                <Text fontSize="2xl" fontWeight="bold" color="white">
                  {archivedContent.filter(c => !c.isPermanent).length}
                </Text>
                <Text fontSize="sm" color="gray.400">
                  Recoverable
                </Text>
              </VStack>
            </HStack>
          </Box>
        </SimpleGrid>

        {/* Bulk Actions */}
        {bulkSelected.length > 0 && (
          <Box bg={cardBg} p={4} borderRadius="lg" border="1px" borderColor={borderColor}>
            <HStack justify="space-between">
              <Text color="white" fontWeight="medium">
                {bulkSelected.length} items selected
              </Text>
              <HStack gap={2}>
                <Select
                  value={archiveReason}
                  onChange={(e) => setArchiveReason(e.target.value)}
                  placeholder="Select reason"
                  bg="gray.700"
                  border="none"
                  color="white"
                  _focus={{ bg: 'gray.600' }}
                  w="200px"
                >
                  {archiveReasons.map(reason => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </Select>
                <Button
                  colorScheme="orange"
                  size="sm"
                  onClick={handleBulkArchive}
                  leftIcon={<IconWrapper icon={FiArchive} size={14} />}
                >
                  Archive Selected
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setBulkSelected([])}
                >
                  Clear
                </Button>
              </HStack>
            </HStack>
          </Box>
        )}

        {/* Archived Content Table */}
        {archivedContent.length === 0 ? (
          <Center py={8}>
            <VStack gap={2}>
              <Text color="gray.500">No archived content found</Text>
              <Text fontSize="sm" color="gray.400">
                Content that has been archived will appear here
              </Text>
            </VStack>
          </Center>
        ) : (
          <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} overflow="hidden">
            <TableContainer>
              <Table variant="simple">
                <Thead bg="gray.700">
                  <Tr>
                    <Th color="white" w="50px">
                      <Checkbox
                        isChecked={bulkSelected.length === archivedContent.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setBulkSelected(archivedContent.map(c => c.id));
                          } else {
                            setBulkSelected([]);
                          }
                        }}
                        colorScheme="purple"
                      />
                    </Th>
                    <Th color="white">Content</Th>
                    <Th color="white">Description</Th>
                    <Th color="white">Archived Date</Th>
                    <Th color="white">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {archivedContent.map(renderContentRow)}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Archive Content Modal */}
        <Modal isOpen={isArchiveModalOpen} onClose={() => setIsArchiveModalOpen(false)}>
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white">
            <ModalHeader>Archive Content</ModalHeader>
            <ModalBody>
              <VStack gap={4}>
                <FormControl>
                  <FormLabel>Archive Reason</FormLabel>
                  <Select
                    value={archiveReason}
                    onChange={(e) => setArchiveReason(e.target.value)}
                    bg="gray.700"
                    border="none"
                    color="white"
                    _focus={{ bg: 'gray.600' }}
                  >
                    <option value="">Select a reason</option>
                    {archiveReasons.map(reason => (
                      <option key={reason} value={reason}>
                        {reason}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl>
                  <Checkbox
                    isChecked={isPermanent}
                    onChange={(e) => setIsPermanent(e.target.checked)}
                    colorScheme="red"
                  >
                    Permanent archive (cannot be recovered)
                  </Checkbox>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => setIsArchiveModalOpen(false)}>
                Cancel
              </Button>
              <Button
                colorScheme="orange"
                onClick={handleArchiveContent}
                isLoading={isArchiving}
                loadingText="Archiving..."
              >
                Archive
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Unarchive Confirmation Modal */}
        <Modal isOpen={isUnarchiveModalOpen} onClose={() => setIsUnarchiveModalOpen(false)}>
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white">
            <ModalHeader>Unarchive Content</ModalHeader>
            <ModalBody>
              <Alert status="info">
                <AlertIcon />
                <AlertDescription>
                  Are you sure you want to unarchive this content? It will be restored to its previous state.
                </AlertDescription>
              </Alert>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => setIsUnarchiveModalOpen(false)}>
                Cancel
              </Button>
              <Button colorScheme="green" onClick={handleUnarchiveContent}>
                Unarchive
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* View Archive Details Modal */}
        <Modal isOpen={isViewingArchive} onClose={() => setIsViewingArchive(false)}>
          <ModalOverlay />
          <ModalContent bg="gray.800" color="white" maxW="2xl">
            <ModalHeader>Archive Details</ModalHeader>
            <ModalBody>
              {selectedContent && (
                <VStack align="stretch" gap={4}>
                  <HStack gap={4}>
                    <Image
                      src={selectedContent.poster}
                      alt={selectedContent.title}
                      w="100px"
                      h="100px"
                      objectFit="cover"
                      borderRadius="md"
                      fallbackSrc="/placeholder-poster.jpg"
                    />
                    <VStack align="start" gap={2}>
                      <Text fontWeight="bold" color="white" fontSize="lg">
                        {selectedContent.title}
                      </Text>
                      <Badge colorScheme={getContentTypeColor(selectedContent.category)}>
                        {selectedContent.category}
                      </Badge>
                      <Text fontSize="sm" color="gray.400">
                        Archived: {formatDate(selectedContent.archivedAt)}
                      </Text>
                    </VStack>
                  </HStack>
                  
                  <Text color="gray.300">
                    {selectedContent.description}
                  </Text>
                </VStack>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" onClick={() => setIsViewingArchive(false)}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Container>
  );
};

export default ContentArchiving;
